'use client'

import * as React from 'react'
import {
  ALL_ACCEPTED,
  CONSENT_EVENT,
  DEFAULT_DENIED,
  PRE_DECISION_DEFAULTS,
  type ConsentCategories,
} from './types'
import { readStoredConsent, writeStoredConsent } from './cookie'
import { setDefaultGtagConsent, updateGtagConsent } from './gtag'

type ConsentContextValue = {
  consent: ConsentCategories | null
  // Effective categories — `consent` when the user has decided, otherwise
  // `PRE_DECISION_DEFAULTS` (functional on, everything else off). Consumers
  // that gate side effects (pixels, chat widget) should read this so the
  // pre-banner state is consistent.
  effective: ConsentCategories
  hasDecided: boolean
  acceptAll: () => void
  rejectAll: () => void
  updateConsent: (categories: Partial<ConsentCategories>) => void
  openSettings: () => void
  settingsOpen: boolean
  closeSettings: () => void
}

const ConsentContext = React.createContext<ConsentContextValue | null>(null)

function emitChange(categories: ConsentCategories) {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: categories }))
  updateGtagConsent(categories)
}

export function ConsentProvider({ children }: { children: React.ReactNode }) {
  // SSR-safe: render with `null` so server HTML matches the first client paint,
  // then hydrate from cookie inside an effect.
  const [consent, setConsent] = React.useState<ConsentCategories | null>(null)
  const [settingsOpen, setSettingsOpen] = React.useState(false)
  const hydratedRef = React.useRef(false)

  React.useEffect(() => {
    if (hydratedRef.current) return
    hydratedRef.current = true
    setDefaultGtagConsent()
    const stored = readStoredConsent()
    if (stored) {
      setConsent(stored.categories)
      updateGtagConsent(stored.categories)
    }
  }, [])

  const persist = React.useCallback((next: ConsentCategories) => {
    const stored = writeStoredConsent(next)
    setConsent(stored.categories)
    emitChange(stored.categories)
  }, [])

  const acceptAll = React.useCallback(() => {
    persist(ALL_ACCEPTED)
    setSettingsOpen(false)
  }, [persist])

  const rejectAll = React.useCallback(() => {
    persist(DEFAULT_DENIED)
    setSettingsOpen(false)
  }, [persist])

  const updateConsent = React.useCallback(
    (categories: Partial<ConsentCategories>) => {
      const base = consent ?? DEFAULT_DENIED
      const merged: ConsentCategories = {
        necessary: true,
        analytics: categories.analytics ?? base.analytics,
        marketing: categories.marketing ?? base.marketing,
        personalization: categories.personalization ?? base.personalization,
        functional: categories.functional ?? base.functional,
      }
      persist(merged)
    },
    [consent, persist],
  )

  const openSettings = React.useCallback(() => setSettingsOpen(true), [])
  const closeSettings = React.useCallback(() => setSettingsOpen(false), [])

  const effective = consent ?? PRE_DECISION_DEFAULTS

  const value = React.useMemo<ConsentContextValue>(
    () => ({
      consent,
      effective,
      hasDecided: consent !== null,
      acceptAll,
      rejectAll,
      updateConsent,
      openSettings,
      settingsOpen,
      closeSettings,
    }),
    [consent, effective, acceptAll, rejectAll, updateConsent, openSettings, settingsOpen, closeSettings],
  )

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>
}

export function useConsent(): ConsentContextValue {
  const ctx = React.useContext(ConsentContext)
  if (!ctx) {
    throw new Error('useConsent must be used within a ConsentProvider')
  }
  return ctx
}
